import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth, API } from "../auth";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Search, Filter, FileDown, Plus, Image as ImageIcon, X } from "lucide-react";
import { parseLocalDate } from "../utils/date";

export default function ReportsList() {
  const { authHeaders, user } = useAuth();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [brandCatalog, setBrandCatalog] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    brand: "", chain: "", user_id: "", date_from: "", date_to: "", search: "",
  });
  const [showFilters, setShowFilters] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await axios.get(`${API}/reports`, { headers: authHeaders(), params });
      setItems(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => {
    load();
    axios.get(`${API}/brands`, { headers: authHeaders() }).then((r) => setBrandCatalog(r.data)).catch(() => {});
    if (user?.role === "admin") {
      axios.get(`${API}/users`, { headers: authHeaders() }).then((r) => setUsers(r.data)).catch(() => {});
    }
  }, []);

  const allBrands = useMemo(() => {
    const set = new Set(brandCatalog.map((b) => b.name));
    items.forEach((i) => i.brand && set.add(i.brand));
    return Array.from(set).sort();
  }, [brandCatalog, items]);

  const clearFilters = () => {
    setFilters({ brand: "", chain: "", user_id: "", date_from: "", date_to: "", search: "" });
    setTimeout(load, 0);
  };

  const exportExcel = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const url = `${API}/reports/export/excel?${params.toString()}`;
      const resp = await axios.get(url, { headers: authHeaders(), responseType: "blob" });
      const blob = new Blob([resp.data], { type: resp.headers["content-type"] });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `tradevisit_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Excel descargado");
    } catch (e) {
      toast.error("Error al exportar");
    }
  };

  return (
    <div className="p-5 md:p-8 lg:p-12 max-w-[1400px] mx-auto" data-testid="reports-list-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="kbd-label mb-2 text-emerald-700">/ Reportes</div>
          <h1 className="font-display text-4xl font-bold tracking-tight text-slate-950">
            Todos los reportes
          </h1>
          <p className="text-slate-500 mt-2">{items.length} resultado{items.length !== 1 && "s"}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="border-slate-200 gap-2" data-testid="toggle-filters-btn">
            <Filter className="w-4 h-4" /> Filtros
          </Button>
          <Button onClick={exportExcel} variant="outline" className="border-slate-200 gap-2" data-testid="export-excel-btn">
            <FileDown className="w-4 h-4" /> Exportar Excel
          </Button>
          <Link to="/reports/new" className="inline-flex items-center gap-2 bg-emerald-700 text-white px-4 py-2 rounded-md hover:bg-emerald-800 font-medium" data-testid="new-report-btn">
            <Plus className="w-4 h-4" /> Nuevo
          </Link>
        </div>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Buscar por sucursal, ciudad, hallazgos, persona…"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          onKeyDown={(e) => e.key === "Enter" && load()}
          className="pl-10 h-12 border-slate-300 rounded-md"
          data-testid="search-input"
        />
      </div>

      {showFilters && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 mb-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="filters-panel">
          <FilterField label="Marca">
            <Select value={filters.brand || "__all"} onValueChange={(v) => setFilters({ ...filters, brand: v === "__all" ? "" : v })}>
              <SelectTrigger data-testid="filter-brand"><SelectValue placeholder="Todas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">Todas</SelectItem>
                {allBrands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          </FilterField>
          <FilterField label="Cadena">
            <Input value={filters.chain} onChange={(e) => setFilters({ ...filters, chain: e.target.value })} data-testid="filter-chain" />
          </FilterField>
          {user?.role === "admin" && (
            <FilterField label="Usuario">
              <Select value={filters.user_id || "__all"} onValueChange={(v) => setFilters({ ...filters, user_id: v === "__all" ? "" : v })}>
                <SelectTrigger data-testid="filter-user"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">Todos</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </FilterField>
          )}
          <FilterField label="Desde">
            <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="filter-date-from" />
          </FilterField>
          <FilterField label="Hasta">
            <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="filter-date-to" />
          </FilterField>
          <div className="col-span-full flex gap-2">
            <Button onClick={load} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="apply-filters-btn">
              Aplicar filtros
            </Button>
            <Button variant="outline" onClick={clearFilters} className="gap-2" data-testid="clear-filters-btn">
              <X className="w-4 h-4" /> Limpiar
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-lg p-16 text-center text-slate-400 kbd-label">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-16 text-center">
          <p className="font-display text-2xl text-slate-900 mb-2">Sin resultados</p>
          <p className="text-sm text-slate-500">Ajusta tus filtros o crea un nuevo reporte.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((r) => (
            <Link
              to={`/reports/${r.id}`}
              key={r.id}
              className="group bg-white border border-slate-200 rounded-lg p-5 hover:border-emerald-600 hover:shadow-sm transition-all"
              data-testid={`report-card-${r.id}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-emerald-700 text-white font-display font-bold flex flex-col items-center justify-center rounded-md">
                    <span className="text-[9px] leading-none opacity-70">
                      {parseLocalDate(r.fecha).toLocaleDateString("es", { month: "short" }).toUpperCase()}
                    </span>
                    <span className="text-lg leading-tight">{parseLocalDate(r.fecha).getDate()}</span>
                  </div>
                  <div>
                    <div className="font-display font-bold text-lg text-slate-950 leading-tight">{r.brand}</div>
                    <div className="kbd-label !text-[10px]">{r.visit_type}</div>
                  </div>
                </div>
                {r.photos?.length > 0 && (
                  <div className="flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                    <ImageIcon className="w-3 h-3" /> {r.photos.length}
                  </div>
                )}
              </div>
              <div className="space-y-1 text-sm">
                <div><span className="kbd-label">Cadena</span><span className="ml-2 text-slate-900">{r.chain}</span></div>
                <div><span className="kbd-label">PDV</span><span className="ml-2 text-slate-900">{r.branch}, {r.city}</span></div>
                <div>
                  <span className="kbd-label">Visita</span>
                  <span className="ml-2 text-slate-900">{r.visitor_name}</span>
                  {r.visitor_position && <span className="ml-1 text-slate-500">· {r.visitor_position}</span>}
                </div>
              </div>
              {(r.competitor_promo_desc || r.comments) && (
                <div className="mt-4 pt-4 border-t border-slate-100 text-sm text-slate-600 line-clamp-2">
                  {r.competitor_promo_desc || r.comments}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div>
      <Label className="kbd-label">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
